import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, Button, Box, Paper, Autocomplete, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { UserService } from '../../services/user.service';
import { InstitutionService } from '../../services/institution.service';
import { AuthService } from '../../services/auth.service';
import { RoleService } from '../../services/role.service';
import { IRole } from '../../interfaces/role.interface';
import { useToast } from '../../context/ToastContext';
import { useIsAdmin } from '../../hooks/usePermission';
import { validatePassword } from '../../utils/passwordPolicy';
import { isFreeTierLimitError } from '../../utils/planErrors';
import UpgradeNudge from '../common/UpgradeNudge';
import { usePlan } from '../../context/PlanContext';
import { helpWhy } from '../../help/why';

const UserCreate = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const isAdmin = useIsAdmin();
    const { refresh: refreshPlan } = usePlan();
    // Merge (develop -> dynamic roles): no `type` field any more -- the global user.type role enum
    // is gone; access comes from the roles selected below.
    const [user, setUser] = useState({ usernameSuffix: '', password: '', phone: '', firstName: '', lastName: '' });
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [institutionCode, setInstitutionCode] = useState<string | null>(null);
    const [isLoadingCode, setIsLoadingCode] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [roles, setRoles] = useState<IRole[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<IRole[]>([]);
    const [showCapNudge, setShowCapNudge] = useState(false);

    // The institution code is already shown as a fixed prefix; if the admin types (or pastes) the
    // full login name "<code>_jdoe" we drop the duplicate prefix instead of creating "<code>_<code>_jdoe".
    const cleanSuffix = (raw: string): string => {
        const value = raw.replace(/\s/g, '');
        if (!institutionCode) return value;
        const prefix = `${institutionCode}_`;
        return value.toLowerCase().startsWith(prefix.toLowerCase()) ? value.slice(prefix.length) : value;
    };

    const effectiveUsername = institutionCode
        ? `${institutionCode}_${user.usernameSuffix}`
        : user.usernameSuffix;
    const passwordErrors = validatePassword(user.password, effectiveUsername, user.phone);

    useEffect(() => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) {
            setIsLoadingCode(false);
            return;
        }
        // GET /institution/{id} is admin-only; a non-admin user manager (HR Manager) reads the code
        // from the caller's own institution list instead (any member may), so no 403 is fired.
        const ownInstitution = () => InstitutionService.getLoggedInUserInstitutions(0, 200).then(
            (page: { content?: { id: string; code: string }[] }) => {
                const found = page?.content?.find(i => i.id === institutionId);
                if (!found) throw new Error('institution not visible');
                return found;
            });
        (isAdmin
            ? InstitutionService.getInstitutionById(institutionId).catch(ownInstitution)
            : ownInstitution())
            .then((data: { code: string }) => {
                setInstitutionCode(data.code);
                setIsLoadingCode(false);
            })
            .catch(() => {
                toast.showError('Failed to load institution details');
                setIsLoadingCode(false);
            });

        // GET /roles is admin-only; USER_MANAGE holders use the assignable-roles endpoint instead.
        (isAdmin ? RoleService.list(institutionId) : RoleService.assignable(institutionId))
            .then((allRoles) => {
                const active = allRoles.filter(r => r.isActive && (isAdmin || !r.isAdmin));
                setRoles(active);
                const employeeRole = active.find(r => r.templateKey === 'EMPLOYEE');
                if (employeeRole) {
                    setSelectedRoles([employeeRole]);
                }
            })
            .catch(() => {
                toast.showError('Failed to load roles');
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    const createWhy = isLoading ? null
        : institutionCode === null ? 'The company details are still loading. Wait a moment.'
        : !user.firstName.trim() || !user.lastName.trim() ? 'Enter the first name and the last name.'
        : !user.usernameSuffix ? 'Enter a username.'
        : !user.password ? 'Enter a password.'
        : !confirmPassword ? 'Type the password again in Confirm Password.'
        : user.password !== confirmPassword ? 'The two passwords do not match.'
        : !user.phone ? 'Enter a phone number.'
        : selectedRoles.length === 0 ? 'Pick at least one role.'
        : passwordErrors.length > 0 ? 'The password is too weak. Read the red note under the Password box.'
        : null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setShowCapNudge(false);

        if (!user.firstName.trim() || !user.lastName.trim() || !user.usernameSuffix || !user.password || !confirmPassword || !user.phone || selectedRoles.length === 0) {
            toast.showError('All fields are required');
            setIsLoading(false);
            return;
        }

        if (user.password !== confirmPassword) {
            toast.showError('Password and confirm password do not match');
            setIsLoading(false);
            return;
        }

        if (passwordErrors.length > 0) {
            toast.showError('Password does not meet the strength requirements');
            setIsLoading(false);
            return;
        }

        try {
            await UserService.createUser({
                username: effectiveUsername,
                password: user.password,
                phone: user.phone,
                firstName: user.firstName.trim(),
                lastName: user.lastName.trim(),
                roleIds: selectedRoles.map(r => r.id)
            });
            refreshPlan();
            toast.showSuccess(`User ${effectiveUsername} created`);
            navigate('/users', { state: { createdUsername: effectiveUsername } });
        } catch (err) {
            if (isFreeTierLimitError(err)) {
                setShowCapNudge(true);
            } else {
                toast.showApiError(err, 'Failed to create user');
            }
            console.error('Error creating user:', err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ padding: '20px' }}>
            <Box sx={{ maxWidth: 600, margin: '0 auto' }}>
                <Paper sx={{ p: 3 }} data-help-page="users.create.page">
                    <h2>Create New User</h2>
                    {showCapNudge && (
                        <UpgradeNudge
                            sx={{ mb: 2 }}
                            message="You've reached your free-tier employee limit. Upgrade your plan to add more employees."
                        />
                    )}
                    <form onSubmit={handleSubmit}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }} data-help-section="users.create.section.name">
                            <TextField
                                data-help="users.create.field.firstName"
                                label="First name"
                                value={user.firstName}
                                onChange={(e) => setUser({ ...user, firstName: e.target.value })}
                                margin="normal"
                                required
                                inputProps={{ maxLength: 60 }}
                                sx={{ flex: '1 1 220px' }}
                            />
                            <TextField
                                data-help="users.create.field.lastName"
                                label="Last name"
                                value={user.lastName}
                                onChange={(e) => setUser({ ...user, lastName: e.target.value })}
                                margin="normal"
                                required
                                inputProps={{ maxLength: 60 }}
                                sx={{ flex: '1 1 220px' }}
                            />
                        </Box>
                        <TextField
                            data-help="users.create.field.username"
                            fullWidth
                            label="Username"
                            value={user.usernameSuffix}
                            onChange={(e) => setUser({ ...user, usernameSuffix: cleanSuffix(e.target.value) })}
                            margin="normal"
                            required
                            InputProps={institutionCode ? {
                                startAdornment: (
                                    <InputAdornment position="start">{institutionCode}_</InputAdornment>
                                )
                            } : {}}
                        />
                        <TextField
                            data-help="users.create.field.password"
                            fullWidth
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            value={user.password}
                            onChange={(e) => setUser({ ...user, password: e.target.value })}
                            margin="normal"
                            required
                            error={user.password.length > 0 && passwordErrors.length > 0}
                            helperText={user.password.length > 0 && passwordErrors.length > 0 ? passwordErrors.join('; ') : 'A one-time password: the user must choose their own at first login.'}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            data-help="users.create.showPassword"
                                            edge="end"
                                            size="small"
                                            aria-label={showPassword ? 'hide password' : 'show password'}
                                        >
                                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            data-help="users.create.field.confirmPassword"
                            fullWidth
                            label="Confirm Password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            margin="normal"
                            required
                            error={confirmPassword.length > 0 && confirmPassword !== user.password}
                            helperText={confirmPassword.length > 0 && confirmPassword !== user.password ? 'Passwords do not match' : ''}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            data-help="users.create.showPassword"
                                            edge="end"
                                            size="small"
                                            aria-label={showConfirmPassword ? 'hide confirm password' : 'show confirm password'}
                                        >
                                            {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            data-help="users.create.field.phone"
                            fullWidth
                            label="Phone"
                            value={user.phone}
                            onChange={(e) => setUser({ ...user, phone: e.target.value })}
                            margin="normal"
                            required
                        />
                        <Autocomplete
                            data-help="users.create.field.roles"
                            multiple
                            options={roles}
                            value={selectedRoles}
                            onChange={(_, v) => setSelectedRoles(v)}
                            getOptionLabel={(r) => r.name}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderInput={(params) => <TextField {...params} label="Roles" margin="normal" />}
                        />
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button
                                type="button"
                                data-help="users.create.cancel"
                                onClick={() => navigate('/users')}
                                variant="outlined"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                data-help="users.create.save"
                                {...helpWhy(createWhy ? { kind: 'status', message: createWhy } : null)}
                                variant="contained"
                                color="primary"
                                disabled={isLoading || institutionCode === null || !user.firstName.trim() || !user.lastName.trim() || !user.usernameSuffix || !user.password || !confirmPassword || user.password !== confirmPassword || !user.phone || selectedRoles.length === 0 || passwordErrors.length > 0}
                            >
                                {isLoading ? <CircularProgress size={24} /> : 'Create User'}
                            </Button>
                        </Box>
                    </form>
                </Paper>
            </Box>
        </div>
    );
};

export default UserCreate;
